import { useState } from "react";
import { Check, Download, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;
const abs = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const usd = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: (c || 0) % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
const dollars = (v) => usd(Math.round((v || 0) * 100));
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const AVAIL = { in_stock: "In stock", backorder: "Backorder", unknown: "Availability to confirm", discontinued: "Discontinued" };

const post = async (path, body) => {
  const r = await fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong");
  return data;
};

const Sec = ({ eyebrow, title, children, testId }) => (
  <section className="cp-sec" data-testid={testId}><p className="cp-eb">{eyebrow}</p><h2 className="cp-h2">{title}</h2>{children}</section>
);
const Row = ({ k, v, strong }) => <div className={`cp-row ${strong ? "is-strong" : ""}`}><span>{k}</span><span>{v}</span></div>;

export const ClientProgress = ({ d }) => {
  const pr = d.progress;
  return (
    <div className="cp-card" data-testid="client-progress">
      <p className="cp-label">Where we are · {pr.done} of {pr.total} steps</p>
      <div className="cp-progress"><i style={{ width: `${Math.round((pr.done / pr.total) * 100)}%`, background: d.branding?.accent }} /></div>
      <p className="cp-p" style={{ marginTop: 10 }} data-testid="client-next-step"><strong>Next:</strong> {d.next_step}</p>
    </div>
  );
};

const Respond = ({ board, token, onDone, products }) => {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const [items, setItems] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const send = async (action) => {
    setBusy(true); setError("");
    try { await post(`/${token}/boards/${board.id}/respond`, { action, feedback: text, item_comments: items }); onDone(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const tid = `client-board-${board.id}`;
  return (
    <div className="cp-respond" data-testid={`${tid}-respond`}>
      {products && (
        <div className="cp-itemcomments">{products.map((p) => (
          <label key={p.id} className="cp-field"><span>Comment on {p.name} (optional)</span>
            <input value={items[p.id] || ""} onChange={(e) => setItems({ ...items, [p.id]: e.target.value })} data-testid={`${tid}-comment-${p.id}`} /></label>))}</div>
      )}
      <label className="cp-field"><span>Your feedback {mode && mode !== "approve" ? "(required)" : "(optional)"}</span>
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid={`${tid}-feedback`} /></label>
      {error && <p className="cp-error" data-testid={`${tid}-error`}>{error}</p>}
      <div className="cp-btnrow">
        <button className="cp-btn" disabled={busy} onClick={() => { setMode("approve"); send("approve"); }} data-testid={`${tid}-approve`}>{products ? "Approve product board" : "Approve"}</button>
        <button className="cp-btn cp-btn--ghost" disabled={busy} onClick={() => { setMode("changes"); send("changes"); }} data-testid={`${tid}-changes`}>Request changes</button>
        {!products && <button className="cp-btn cp-btn--ghost" disabled={busy} onClick={() => { setMode("not_my_style"); send("not_my_style"); }} data-testid={`${tid}-not-my-style`}>Not my style</button>}
      </div>
    </div>
  );
};

const Files = ({ v }) => (
  <div className="cp-boardfiles">
    {v.files.map((f) => (f.content_type === "application/pdf"
      ? <a key={f.id} className="cp-btn cp-btn--ghost" href={abs(f.url)} target="_blank" rel="noreferrer" data-testid={`client-file-${f.id}`}><FileText size={15} /> {f.original_name}</a>
      : <a key={f.id} href={abs(f.url)} target="_blank" rel="noreferrer" data-testid={`client-file-${f.id}`}><img src={abs(f.url)} alt={f.original_name} loading="lazy" /></a>))}
  </div>
);

const History = ({ b }) => (b.approvals.length > 0 || b.versions.length > 1) && (
  <details className="cp-terms"><summary>History</summary>
    {b.approvals.map((a) => <p key={a.id} className="cp-small">{fmt(a.at)} · v{a.version} · {a.label}{a.feedback ? ` — “${a.feedback}”` : ""}</p>)}
    {b.versions.slice(0, -1).map((v) => <div key={v.id}><p className="cp-small">Earlier version v{v.version} · {fmt(v.sent_at)}</p><Files v={v} /></div>)}
  </details>
);

export const ConceptBoards = ({ boards, token, onDone }) => boards.map((b) => {
  const v = b.versions[b.versions.length - 1];
  return (
    <Sec key={b.id} eyebrow={`Concept mood board · v${v.version} · ${b.rooms.join(", ")}`} title={b.title} testId={`client-board-${b.id}`}>
      <p className="cp-small" data-testid={`client-board-${b.id}-status`}>{b.status === "approved" ? <><Check size={13} /> Approved</> : b.status === "sent" ? "Ready for your review" : "Thank you — we're working on your feedback"} · {b.revisions_label}</p>
      <Files v={v} />
      {b.status === "sent" && <Respond board={b} token={token} onDone={onDone} />}
      <History b={b} />
    </Sec>
  );
});

export const ProductBoard = ({ b, token, onDone }) => {
  const v = b.versions[b.versions.length - 1];
  const rooms = b.products.reduce((m, p) => ({ ...m, [p.room_label || "Unassigned"]: [...(m[p.room_label || "Unassigned"] || []), p] }), {});
  return (
    <Sec eyebrow={`Product board · v${v.version}`} title="Your furniture & products" testId="client-product-board">
      <p className="cp-small" data-testid="client-product-status">{b.status === "approved" ? "Approved" : b.status === "sent" ? "Ready for your review" : "We're updating your board"} · {b.revisions_label}</p>
      {v.files.length > 0 && <Files v={v} />}
      {Object.entries(rooms).map(([room, list]) => (
        <div key={room} className="cp-card"><p className="cp-label">{room} · {dollars(b.totals.rooms[room] || 0)}</p>
          {list.map((p) => (
            <div key={p.id} className="cp-product" data-testid={`client-product-${p.id}`}>
              {p.image ? <img src={abs(p.image)} alt={p.name} loading="lazy" /> : <span />}
              <div><strong>{p.name}</strong><p className="cp-small">{[p.vendor, p.dimensions, p.finish, AVAIL[p.availability]].filter(Boolean).join(" · ")}</p>{p.notes && <p className="cp-small">{p.notes}</p>}</div>
              <span>{p.quantity} × {dollars(p.price)}</span>
            </div>))}
        </div>
      ))}
      <Row k="Estimated furniture cost — separate from hausA design fees" v={dollars(b.totals.project)} strong />
      <p className="cp-small">Approving the board doesn't purchase anything.</p>
      {b.status === "sent" && <Respond board={b} token={token} onDone={onDone} products={b.products} />}
      <History b={b} />
    </Sec>
  );
};

const Authorize = ({ d, token, onDone }) => {
  const [name, setName] = useState("");
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");
  return (
    <div className="cp-card" data-testid="client-authorize">
      <p className="cp-p">Please authorize hausA to purchase the approved items (estimated {dollars(d.order.total ?? d.order.totals?.project)}). Nothing is charged by this confirmation.</p>
      <label className="cp-field"><span>Type your full name ({d.client.full_name})</span><input value={name} onChange={(e) => setName(e.target.value)} data-testid="authorize-name" /></label>
      <label className="cp-check"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="authorize-check" /> I authorize hausA to place these orders on my behalf</label>
      {error && <p className="cp-error">{error}</p>}
      <button className="cp-btn" disabled={!ok || name.trim().length < 2} onClick={() => post(`/${token}/order/authorize`, { full_name: name, confirm: ok }).then(onDone).catch((e) => setError(e.message))} data-testid="authorize-submit">Authorize purchases</button>
    </div>
  );
};

export const OrderSection = ({ d, token, onDone }) => {
  const o = d.order;
  if (!o) return null;
  if (!o.released_at) {
    return (
      <Sec eyebrow="Ordering" title={o.mode === "client" ? "Your shopping list" : "Your orders"} testId="client-order-locked">
        <p className="cp-p">{o.mode === "client" ? "Your shopping list is released once the final balance is received." : "We place your orders once the final balance is received."}</p>
        {o.mode === "hausa" && !o.authorization && <Authorize d={d} token={token} onDone={onDone} />}
        {o.mode === "hausa" && o.authorization && <p className="cp-small" data-testid="client-authorized"><Check size={13} /> Purchases authorized</p>}
      </Sec>
    );
  }
  return (
    <Sec eyebrow={o.mode === "client" ? "Shopping list" : "Order status"} title={o.mode === "client" ? "Your shopping list" : "Ordering & delivery"} testId="client-order">
      {o.mode === "client" && <div className="cp-dl">
        <a className="cp-btn cp-btn--ghost" href={`${API}/${token}/shopping-list.pdf`} target="_blank" rel="noreferrer" data-testid="client-list-pdf"><Download size={15} /> Shopping list (PDF)</a>
        <a className="cp-btn cp-btn--ghost" href={`${API}/${token}/shopping-list.csv`} data-testid="client-list-csv"><Download size={15} /> Spreadsheet (CSV)</a></div>}
      <div className="cp-card">
        {o.items.map((it) => (
          <div key={it.item_id} className="cp-product" data-testid={`client-item-${it.item_id}`}>
            {it.image ? <img src={abs(it.image)} alt={it.name} loading="lazy" /> : <span />}
            <div><strong>{it.name}</strong><p className="cp-small">{[it.room_label, it.vendor, it.dimensions, it.finish].filter(Boolean).join(" · ")}</p>
              {o.mode === "client" ? it.url && <a className="cp-link" href={it.url} target="_blank" rel="noreferrer">Buy from {it.vendor || "store"}</a>
                : <p className="cp-small" data-testid={`client-item-${it.item_id}-status`}>{it.status}{it.expected_delivery ? ` · expected ${fmt(it.expected_delivery)}` : ""}</p>}</div>
            <span>{it.quantity} × {dollars(it.price)}</span>
          </div>))}
        <Row k="Estimated furniture cost — separate from hausA design fees" v={dollars(o.totals.project)} strong />
      </div>
    </Sec>
  );
};

export const Services = ({ list }) => list.length > 0 && (
  <Sec eyebrow="Optional services" title="Extra services" testId="client-services">
    <div className="cp-card">{list.map((s) => <Row key={s.id} k={`${s.label}${s.basis ? ` · ${s.basis}` : ""}${s.appointment_date ? ` · ${fmt(s.appointment_date)}` : ""}${s.vendor_name ? ` · ${s.vendor_name}` : ""}`} v={usd(s.amount_cents)} />)}
      <p className="cp-small">Separate from design fees and furniture. Billed only when we send you a payment request.</p></div>
  </Sec>
);

export const BalanceCard = ({ b }) => b.milestones && (
  <div className="cp-card" data-testid="client-balances">
    {[["deposit", "Initial deposit"], ["concept", "Concept payment"], ["final", "Final balance"]].map(([k, l]) => (
      <Row key={k} k={`${l} · ${usd(b.milestones[k].amount)}`} v={b.milestones[k].outstanding ? `${usd(b.milestones[k].outstanding)} due` : "Paid"} />))}
    <Row k={`Total design fee · ${usd(b.total)}`} v={`Paid ${usd(b.paid_total)}`} strong />
  </div>
);

export const Documents = ({ docs }) => docs.length > 0 && (
  <div className="cp-dl" data-testid="client-documents">
    {docs.map((x, i) => <a key={`${x.title}-${i}`} className="cp-btn cp-btn--ghost" href={abs(x.url)} target="_blank" rel="noreferrer" data-testid={`client-doc-${i}`}><Download size={15} /> {x.title}</a>)}
  </div>
);
